import type {
  AdminStats,
  PlaceCandidate,
  ShareLink,
  Trip,
  TripItem,
  TripWithItems,
  User,
  ItemType,
} from '@shared/types';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (!res.ok) {
    throw new ApiError(
      res.status,
      data?.error ?? 'unknown',
      data?.message ?? 'Something went wrong.',
    );
  }
  return data as T;
}

export interface TripSummary extends Trip {
  itemCount: number;
  owner?: { id: string; name: string | null; avatarUrl: string | null };
}

export interface GateResponse {
  gated: true;
  enforced: boolean;
  message: string;
}

const isGate = (v: unknown): v is GateResponse =>
  typeof v === 'object' && v !== null && 'gated' in v;

export const api = {
  me: () => request<{ user: User | null }>('/api/auth/me'),

  adminStats: () => request<AdminStats>('/api/admin/stats'),

  authConfig: () => request<{ google: boolean; apple: boolean }>('/api/auth/config'),

  signup: (body: { email: string; password: string; name?: string }) =>
    request<{ user: User; redirect: string | null }>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  login: (body: { email: string; password: string }) =>
    request<{ user: User; redirect: string | null }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  listTrips: () => request<{ mine: TripSummary[]; saved: TripSummary[] }>('/api/trips'),

  getTripBySlug: (slug: string, token?: string | null) =>
    request<TripWithItems>(
      `/api/trips/by-slug/${encodeURIComponent(slug)}${token ? `?k=${encodeURIComponent(token)}` : ''}`,
    ),

  getTrip: (id: string) => request<TripWithItems>(`/api/trips/${id}`),

  createTrip: (body: {
    title: string;
    destination?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    coverImage?: string | null;
  }) => request<TripWithItems>('/api/trips', { method: 'POST', body: JSON.stringify(body) }),

  updateTrip: (id: string, body: Partial<Trip>) =>
    request<TripWithItems>(`/api/trips/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),

  deleteTrip: (id: string) => request<{ ok: true }>(`/api/trips/${id}`, { method: 'DELETE' }),

  placesConfigured: () => request<{ configured: boolean }>('/api/places/config'),

  importConfigured: () => request<{ configured: boolean }>('/api/trips/import/config'),

  /** Kick background enrichment (link previews, photos) with a fresh request. */
  enrichTrip: (tripId: string) =>
    request<{ ok: true; enriching: number }>(`/api/trips/${tripId}/enrich`, { method: 'POST' }),

  /** One turn of the chat builder; returns the reply and a full item snapshot. */
  chatTrip: (tripId: string, messages: Array<{ role: 'user' | 'assistant'; text: string }>) =>
    request<{ reply: string; items: TripItem[] }>(`/api/trips/${tripId}/chat`, {
      method: 'POST',
      body: JSON.stringify({ messages }),
    }),

  /** Paste the owner's own writing; the server sorts it into cards. */
  importTrip: (tripId: string, text: string) =>
    request<{ count: number; items: TripItem[] }>(`/api/trips/${tripId}/import`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),

  searchPlaces: (q: string, bias?: { lat: number; lng: number } | null) => {
    const params = new URLSearchParams({ q });
    if (bias) {
      params.set('lat', String(bias.lat));
      params.set('lng', String(bias.lng));
    }
    return request<{ results: PlaceCandidate[] }>(`/api/places/search?${params}`);
  },

  addItem: (
    tripId: string,
    body: {
      type: ItemType;
      url?: string | null;
      note?: string | null;
      ogTitle?: string | null;
      placeId?: string | null;
      placeName?: string | null;
      placeAddress?: string | null;
      mapUrl?: string | null;
      placePhotoRef?: string | null;
      imageUrl?: string | null;
      lat?: number | null;
      lng?: number | null;
    },
  ) =>
    request<TripItem>(`/api/trips/${tripId}/items`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateItem: (
    tripId: string,
    itemId: string,
    body: Partial<TripItem> & { placePhotoRef?: string | null; imageUrl?: string | null },
  ) =>
    request<TripItem>(`/api/trips/${tripId}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  deleteItem: (tripId: string, itemId: string) =>
    request<{ ok: true }>(`/api/trips/${tripId}/items/${itemId}`, { method: 'DELETE' }),

  reorderItems: (tripId: string, itemIds: string[]) =>
    request<{ ok: true }>(`/api/trips/${tripId}/items/order`, {
      method: 'PUT',
      body: JSON.stringify({ itemIds }),
    }),

  /** Returns a gate descriptor when the viewer is anonymous. */
  saveTrip: async (tripId: string, token?: string | null) => {
    const result = await request<{ ok: true; saved: boolean } | GateResponse>(
      `/api/trips/${tripId}/save${token ? `?k=${encodeURIComponent(token)}` : ''}`,
      { method: 'POST' },
    );
    return isGate(result) ? result : { ...result, gated: false as const };
  },

  unsaveTrip: (tripId: string) =>
    request<{ ok: true; saved: boolean }>(`/api/trips/${tripId}/save`, { method: 'DELETE' }),

  cloneTrip: async (tripId: string, token?: string | null) => {
    const result = await request<TripWithItems | GateResponse>(
      `/api/trips/${tripId}/clone${token ? `?k=${encodeURIComponent(token)}` : ''}`,
      { method: 'POST' },
    );
    return isGate(result) ? result : { ...result, gated: false as const };
  },

  listShareLinks: (tripId: string) =>
    request<{ links: ShareLink[] }>(`/api/trips/${tripId}/share-links`),

  createShareLink: (tripId: string, body: { expiresInDays?: number | null } = {}) =>
    request<ShareLink>(`/api/trips/${tripId}/share-links`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  revokeShareLink: (tripId: string, token: string) =>
    request<{ ok: true }>(`/api/trips/${tripId}/share-links/${encodeURIComponent(token)}`, {
      method: 'DELETE',
    }),

  invite: (tripId: string, emails: string[]) =>
    request<{ ok: true; invited: number; bound: number; note?: string }>(
      `/api/trips/${tripId}/invites`,
      { method: 'POST', body: JSON.stringify({ emails }) },
    ),

  notifications: () =>
    request<{
      notifications: Array<{
        id: string;
        type: string;
        readAt: number | null;
        createdAt: number;
        actor: { name: string | null; avatarUrl: string | null };
        trip: { title: string | null; slug: string | null };
      }>;
      unread: number;
    }>('/api/notifications'),

  markNotificationsRead: () =>
    request<{ ok: true }>('/api/notifications/read', { method: 'POST' }),
};
